import { useMemo, useState } from "react";
import axios from "axios";
import { LockKey, Paperclip, ShieldCheck } from "@phosphor-icons/react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { usePageSeo } from "@/hooks/usePageSeo";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const intakeCategories = [
  "Public corruption",
  "Police misconduct",
  "Court or DA issue",
  "School district issue",
  "Transit or public works issue",
  "Records or documents",
  "Community safety issue",
  "Other",
];

const sensitivityOptions = ["Routine", "Sensitive", "High risk"];
const sourceRelationshipOptions = ["Direct witness", "Current employee", "Former employee", "Relative or advocate", "Anonymous document source", "Other"];
const defaultPortalForm = {
  firstName: "",
  lastName: "",
  phone: "",
  email: "",
  allowContact: "Yes",
  tipCategory: intakeCategories[0],
  sensitivityLevel: sensitivityOptions[1],
  sourceRelationship: sourceRelationshipOptions[0],
  involvedEntity: "",
  incidentLocation: "",
  incidentDate: "",
  evidenceSummary: "",
  details: "",
};

const defaultLookupForm = { trackingId: "", accessKey: "" };
const statusToneMap = {
  new: "bg-[#F8F9FA] text-slate-700 border-[#D9DCE1]",
  triaged: "bg-[rgba(19,56,190,0.08)] text-[#1338BE] border-[rgba(19,56,190,0.16)]",
  verifying: "bg-[rgba(59,177,67,0.12)] text-[#2C8F37] border-[rgba(59,177,67,0.2)]",
  "follow-up-needed": "bg-[rgba(210,20,4,0.08)] text-[#D21404] border-[rgba(210,20,4,0.14)]",
  pursuing: "bg-[#0A0A0A] text-white border-[#0A0A0A]",
  resolved: "bg-[rgba(59,177,67,0.12)] text-[#2C8F37] border-[rgba(59,177,67,0.2)]",
  closed: "bg-[#F8F9FA] text-slate-600 border-[#D9DCE1]",
};

const formatPortalTime = (value) => {
  if (!value) {
    return "Just now";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
};

export const SubmitTipPage = () => {
  const [portalForm, setPortalForm] = useState(defaultPortalForm);
  const [lookupForm, setLookupForm] = useState(defaultLookupForm);
  const [portalAttachments, setPortalAttachments] = useState([]);
  const [followUpAttachments, setFollowUpAttachments] = useState([]);
  const [followUpMessage, setFollowUpMessage] = useState("");
  const [submittingPortal, setSubmittingPortal] = useState(false);
  const [loadingCase, setLoadingCase] = useState(false);
  const [sendingFollowUp, setSendingFollowUp] = useState(false);
  const [createdCase, setCreatedCase] = useState(null);
  const [portalCase, setPortalCase] = useState(null);

  usePageSeo({
    canonicalPath: "/submit-a-tip",
    description: "Use the Content Watch HQ investigative portal to submit sensitive tips, receive a tracking ID, and send protected follow-up messages.",
    title: "Investigative Portal | Content Watch HQ",
  });

  const attachmentNames = useMemo(() => portalAttachments.map((file) => file.name), [portalAttachments]);
  const followUpAttachmentNames = useMemo(() => followUpAttachments.map((file) => file.name), [followUpAttachments]);

  const updatePortalField = (field) => (event) => {
    const value = event.target.value;
    setPortalForm((current) => ({ ...current, [field]: value }));
  };

  const updateLookupField = (field) => (event) => {
    const value = event.target.value;
    setLookupForm((current) => ({ ...current, [field]: value }));
  };

  const handlePortalSubmit = async (event) => {
    event.preventDefault();
    setSubmittingPortal(true);
    try {
      const payload = new FormData();
      payload.append("first_name", portalForm.firstName);
      payload.append("last_name", portalForm.lastName);
      payload.append("phone", portalForm.phone);
      payload.append("email", portalForm.email);
      payload.append("allow_contact", portalForm.allowContact);
      payload.append("tip_category", portalForm.tipCategory);
      payload.append("sensitivity_level", portalForm.sensitivityLevel);
      payload.append("source_relationship", portalForm.sourceRelationship);
      payload.append("involved_entity", portalForm.involvedEntity);
      payload.append("incident_location", portalForm.incidentLocation);
      payload.append("incident_date", portalForm.incidentDate);
      payload.append("evidence_summary", portalForm.evidenceSummary);
      payload.append("details", portalForm.details);
      payload.append("source_page", "submit-a-tip");
      portalAttachments.forEach((file) => payload.append("attachments", file));
      const response = await axios.post(`${API_BASE}/submissions/investigative-intake`, payload, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setCreatedCase(response.data);
      setLookupForm({ trackingId: response.data.tracking_id, accessKey: response.data.access_key });
      setPortalCase(null);
      setPortalForm(defaultPortalForm);
      setPortalAttachments([]);
      toast.success("Secure case created. Save your tracking ID and case key.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "The investigative portal did not save your case.");
    } finally {
      setSubmittingPortal(false);
    }
  };

  const handleLookup = async (event) => {
    event.preventDefault();
    setLoadingCase(true);
    try {
      const response = await axios.get(`${API_BASE}/submissions/investigative-portal/${lookupForm.trackingId}`, {
        params: { access_key: lookupForm.accessKey },
      });
      setPortalCase(response.data);
      setCreatedCase(null);
      toast.success("Case loaded.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "We could not load that case.");
    } finally {
      setLoadingCase(false);
    }
  };

  const handleFollowUp = async (event) => {
    event.preventDefault();
    if (!portalCase) {
      return;
    }
    setSendingFollowUp(true);
    try {
      const payload = new FormData();
      payload.append("access_key", lookupForm.accessKey);
      payload.append("message", followUpMessage);
      followUpAttachments.forEach((file) => payload.append("attachments", file));
      const response = await axios.post(`${API_BASE}/submissions/investigative-portal/${portalCase.tracking_id}/messages`, payload, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPortalCase(response.data);
      setFollowUpMessage("");
      setFollowUpAttachments([]);
      toast.success("Follow-up sent to the newsroom.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Follow-up could not be sent.");
    } finally {
      setSendingFollowUp(false);
    }
  };

  return (
    <main className="page-shell mx-auto max-w-7xl px-4 py-12 sm:px-6 sm:py-14 lg:px-8 lg:py-16" data-testid="submit-tip-page">
      <section className="grid gap-8 border border-[#D9DCE1] bg-white p-8 shadow-[0_20px_60px_rgba(15,23,42,0.04)] lg:grid-cols-[minmax(0,1.15fr)_380px] lg:p-10" data-testid="submit-tip-layout">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#D21404]" data-testid="submit-tip-overline">Investigative portal</p>
          <h1 className="mt-3 font-[Bebas_Neue] text-5xl uppercase tracking-[0.04em] text-[#0A0A0A] sm:text-6xl" data-testid="submit-tip-title">Send protected leads, then keep talking through one tracked case</h1>
          <p className="mt-4 max-w-3xl text-base leading-8 text-slate-700" data-testid="submit-tip-description">This intake is built for whistleblowers, sensitive documents, and long-running investigative follow-up — not just one-time tips.</p>
          <div className="mt-8 grid gap-4 md:grid-cols-2" data-testid="submit-tip-feature-grid">
            <div className="border border-[#D9DCE1] bg-[#F8F9FA] p-5" data-testid="submit-tip-protection-panel">
              <p className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-[#1338BE]"><ShieldCheck size={16} weight="fill" /> Protected intake</p>
              <p className="mt-3 text-sm leading-7 text-slate-700">Every investigative case gets a tracking ID, a case key, and a private follow-up thread you can return to later.</p>
            </div>
            <div className="border border-[#D9DCE1] bg-[#0A0A0A] p-5 text-white" data-testid="submit-tip-tracking-panel">
              <p className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-[#D21404]"><LockKey size={16} weight="fill" /> Case tracking</p>
              <p className="mt-3 text-sm leading-7 text-slate-200">If the newsroom needs more documents or clarification, replies stay inside the same case instead of getting lost in email.</p>
            </div>
          </div>

          <form className="mt-10 grid gap-4" data-testid="submit-tip-form" onSubmit={handlePortalSubmit}>
            <div className="grid gap-4 sm:grid-cols-2">
              <input className="border border-[rgba(10,15,26,0.12)] bg-white px-4 py-4 text-sm text-slate-900 outline-none focus:border-slate-950" data-testid="submit-tip-first-name-input" onChange={updatePortalField("firstName")} placeholder="First name (optional)" type="text" value={portalForm.firstName} />
              <input className="border border-[rgba(10,15,26,0.12)] bg-white px-4 py-4 text-sm text-slate-900 outline-none focus:border-slate-950" data-testid="submit-tip-last-name-input" onChange={updatePortalField("lastName")} placeholder="Last name (optional)" type="text" value={portalForm.lastName} />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <input className="border border-[rgba(10,15,26,0.12)] bg-white px-4 py-4 text-sm text-slate-900 outline-none focus:border-slate-950" data-testid="submit-tip-phone-input" onChange={updatePortalField("phone")} placeholder="Phone (optional)" type="tel" value={portalForm.phone} />
              <input className="border border-[rgba(10,15,26,0.12)] bg-white px-4 py-4 text-sm text-slate-900 outline-none focus:border-slate-950" data-testid="submit-tip-email-input" onChange={updatePortalField("email")} placeholder="Email (optional if staying anonymous)" type="email" value={portalForm.email} />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <select className="border border-[rgba(10,15,26,0.12)] bg-white px-4 py-4 text-sm text-slate-900 outline-none focus:border-slate-950" data-testid="submit-tip-category-select" onChange={updatePortalField("tipCategory")} value={portalForm.tipCategory}>
                {intakeCategories.map((option) => <option key={option} value={option}>{option}</option>)}
              </select>
              <select className="border border-[rgba(10,15,26,0.12)] bg-white px-4 py-4 text-sm text-slate-900 outline-none focus:border-slate-950" data-testid="submit-tip-sensitivity-select" onChange={updatePortalField("sensitivityLevel")} value={portalForm.sensitivityLevel}>
                {sensitivityOptions.map((option) => <option key={option} value={option}>{option}</option>)}
              </select>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <select className="border border-[rgba(10,15,26,0.12)] bg-white px-4 py-4 text-sm text-slate-900 outline-none focus:border-slate-950" data-testid="submit-tip-source-relationship-select" onChange={updatePortalField("sourceRelationship")} value={portalForm.sourceRelationship}>
                {sourceRelationshipOptions.map((option) => <option key={option} value={option}>{option}</option>)}
              </select>
              <input className="border border-[rgba(10,15,26,0.12)] bg-white px-4 py-4 text-sm text-slate-900 outline-none focus:border-slate-950" data-testid="submit-tip-entity-input" onChange={updatePortalField("involvedEntity")} placeholder="Agency, office, company, or subject involved" type="text" value={portalForm.involvedEntity} />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <input className="border border-[rgba(10,15,26,0.12)] bg-white px-4 py-4 text-sm text-slate-900 outline-none focus:border-slate-950" data-testid="submit-tip-location-input" onChange={updatePortalField("incidentLocation")} placeholder="Incident location or district" type="text" value={portalForm.incidentLocation} />
              <input className="border border-[rgba(10,15,26,0.12)] bg-white px-4 py-4 text-sm text-slate-900 outline-none focus:border-slate-950" data-testid="submit-tip-incident-date-input" onChange={updatePortalField("incidentDate")} type="datetime-local" value={portalForm.incidentDate} />
            </div>
            <input className="border border-[rgba(10,15,26,0.12)] bg-white px-4 py-4 text-sm text-slate-900 outline-none focus:border-slate-950" data-testid="submit-tip-evidence-summary-input" onChange={updatePortalField("evidenceSummary")} placeholder="Short evidence summary: bodycam, email chain, screenshots, invoices, witness notes" type="text" value={portalForm.evidenceSummary} />
            <textarea className="min-h-[13rem] border border-[rgba(10,15,26,0.12)] bg-white px-4 py-4 text-sm text-slate-900 outline-none focus:border-slate-950" data-testid="submit-tip-details-textarea" onChange={updatePortalField("details")} placeholder="Explain what happened, who was involved, why it matters, and what the newsroom should verify next" required value={portalForm.details} />

            <div className="border border-[rgba(10,15,26,0.12)] bg-white px-4 py-4" data-testid="submit-tip-attachment-field">
              <label className="flex items-center gap-2 text-sm font-semibold text-slate-800" htmlFor="submit-tip-attachments">
                <Paperclip size={18} weight="bold" />
                Attach photos, screenshots, or documents
              </label>
              <input accept="image/*,.pdf,.doc,.docx,.txt,.csv" className="mt-3 block w-full text-sm text-slate-700" data-testid="submit-tip-attachments-input" id="submit-tip-attachments" multiple onChange={(event) => setPortalAttachments(Array.from(event.target.files || []))} type="file" />
              {attachmentNames.length ? (
                <div className="mt-4 flex flex-wrap gap-2" data-testid="submit-tip-attachments-list">
                  {attachmentNames.map((name) => (
                    <span key={name} className="border border-[rgba(10,15,26,0.12)] bg-[#F8F9FA] px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-700" data-testid={`submit-tip-attachment-name-${name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                      {name}
                    </span>
                  ))}
                </div>
              ) : null}
            </div>

            <div className="border border-[rgba(10,15,26,0.12)] bg-white px-4 py-4" data-testid="submit-tip-allow-contact-field">
              <p className="text-sm font-semibold text-slate-800">Can the newsroom contact you directly if needed?</p>
              <div className="mt-3 flex flex-wrap gap-6 text-sm text-slate-700">
                <label className="inline-flex items-center gap-2" data-testid="submit-tip-allow-contact-yes-option">
                  <input checked={portalForm.allowContact === "Yes"} name="tip-allow-contact" onChange={updatePortalField("allowContact")} type="radio" value="Yes" />
                  Yes
                </label>
                <label className="inline-flex items-center gap-2" data-testid="submit-tip-allow-contact-no-option">
                  <input checked={portalForm.allowContact === "No"} name="tip-allow-contact" onChange={updatePortalField("allowContact")} type="radio" value="No" />
                  No
                </label>
              </div>
            </div>

            <Button className="rounded-none bg-[#D21404] px-5 py-6 text-xs font-bold uppercase tracking-[0.2em] text-white hover:bg-[#B31203]" data-testid="submit-tip-submit-button" disabled={submittingPortal} type="submit">
              {submittingPortal ? "Securing Case" : "Create Protected Case"}
            </Button>
          </form>
        </div>

        <aside className="space-y-6" data-testid="submit-tip-sidebar">
          <section className="border border-[#D9DCE1] bg-[#F8F9FA] p-6" data-testid="submit-tip-case-lookup-panel">
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#1338BE]">Return to a case</p>
            <h2 className="mt-3 font-[Bebas_Neue] text-3xl uppercase tracking-[0.04em] text-[#0A0A0A]">Use your tracking ID and case key</h2>
            <form className="mt-5 grid gap-4" data-testid="submit-tip-case-lookup-form" onSubmit={handleLookup}>
              <input className="border border-[rgba(10,15,26,0.12)] bg-white px-4 py-4 text-sm text-slate-900 outline-none focus:border-slate-950" data-testid="submit-tip-tracking-id-input" onChange={updateLookupField("trackingId")} placeholder="Tracking ID" required type="text" value={lookupForm.trackingId} />
              <input className="border border-[rgba(10,15,26,0.12)] bg-white px-4 py-4 text-sm text-slate-900 outline-none focus:border-slate-950" data-testid="submit-tip-access-key-input" onChange={updateLookupField("accessKey")} placeholder="Case key" required type="text" value={lookupForm.accessKey} />
              <Button className="rounded-none bg-[#1338BE] px-5 py-6 text-xs font-bold uppercase tracking-[0.2em] text-white hover:bg-[#102F9B]" data-testid="submit-tip-case-lookup-button" disabled={loadingCase} type="submit">
                {loadingCase ? "Opening Case" : "Open Case"}
              </Button>
            </form>
          </section>

          {createdCase ? (
            <section className="border border-[#D21404] bg-[#D21404] p-6 text-white" data-testid="submit-tip-created-case-panel">
              <p className="text-xs font-bold uppercase tracking-[0.22em] text-white/85">Save this now</p>
              <h2 className="mt-3 font-[Bebas_Neue] text-3xl uppercase tracking-[0.04em]">Your case is live</h2>
              <div className="mt-5 space-y-4">
                <div data-testid="submit-tip-created-tracking-id">
                  <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-white/75">Tracking ID</p>
                  <p className="mt-1 text-xl font-bold tracking-[0.1em]">{createdCase.tracking_id}</p>
                </div>
                <div data-testid="submit-tip-created-access-key">
                  <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-white/75">Case key</p>
                  <p className="mt-1 text-xl font-bold tracking-[0.1em]">{createdCase.access_key}</p>
                </div>
              </div>
            </section>
          ) : null}

          {portalCase ? (
            <section className="border border-[#D9DCE1] bg-white p-6" data-testid="submit-tip-case-thread-panel">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#D21404]">Case thread</p>
                  <h2 className="mt-2 font-[Bebas_Neue] text-3xl uppercase tracking-[0.04em] text-[#0A0A0A]" data-testid="submit-tip-case-thread-title">{portalCase.tracking_id}</h2>
                </div>
                <span className={`border px-3 py-2 text-[11px] font-bold uppercase tracking-[0.16em] ${statusToneMap[portalCase.status] || statusToneMap.new}`} data-testid="submit-tip-case-status-badge">
                  {portalCase.status}
                </span>
              </div>
              <div className="mt-5 grid gap-3 sm:grid-cols-2" data-testid="submit-tip-case-meta-grid">
                <div className="border border-[#D9DCE1] bg-[#F8F9FA] p-4" data-testid="submit-tip-case-category-card">
                  <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-slate-500">Category</p>
                  <p className="mt-2 text-sm font-semibold text-slate-900">{portalCase.tip_category || "Investigative case"}</p>
                </div>
                <div className="border border-[#D9DCE1] bg-[#F8F9FA] p-4" data-testid="submit-tip-case-message-count-card">
                  <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-slate-500">Messages</p>
                  <p className="mt-2 text-sm font-semibold text-slate-900">{portalCase.message_count}</p>
                </div>
              </div>
              <div className="mt-6 space-y-4" data-testid="submit-tip-case-messages-list">
                {portalCase.messages.map((message, index) => (
                  <article key={message.id || `${message.sender}-${index}`} className={`border p-4 ${message.sender === "newsroom" ? "border-[rgba(19,56,190,0.16)] bg-[rgba(19,56,190,0.05)]" : "border-[#D9DCE1] bg-[#F8F9FA]"}`} data-testid={`submit-tip-case-message-${index}`}>
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-slate-500">{message.sender === "newsroom" ? "Newsroom" : "Source"}</p>
                      <p className="text-[11px] uppercase tracking-[0.16em] text-slate-500">{formatPortalTime(message.created_at)}</p>
                    </div>
                    <p className="mt-3 whitespace-pre-line text-sm leading-7 text-slate-700">{message.body}</p>
                    {message.file_names?.length ? (
                      <div className="mt-4 flex flex-wrap gap-2" data-testid={`submit-tip-case-message-files-${index}`}>
                        {message.file_names.map((name, fileIndex) => (
                          <span key={`${message.id}-${name}-${fileIndex}`} className="border border-[#D9DCE1] bg-white px-3 py-1 text-[11px] font-bold uppercase tracking-[0.14em] text-[#1338BE]" data-testid={`submit-tip-case-message-file-${index}-${fileIndex}`}>
                            {name}
                          </span>
                        ))}
                      </div>
                    ) : null}
                  </article>
                ))}
              </div>

              <form className="mt-6 grid gap-4 border-t border-[#D9DCE1] pt-6" data-testid="submit-tip-follow-up-form" onSubmit={handleFollowUp}>
                <textarea className="min-h-[9rem] border border-[rgba(10,15,26,0.12)] bg-white px-4 py-4 text-sm text-slate-900 outline-none focus:border-slate-950" data-testid="submit-tip-follow-up-textarea" onChange={(event) => setFollowUpMessage(event.target.value)} placeholder="Send more details, answer newsroom questions, or attach more evidence" required value={followUpMessage} />
                <div className="border border-[rgba(10,15,26,0.12)] bg-[#F8F9FA] px-4 py-4" data-testid="submit-tip-follow-up-attachment-field">
                  <label className="flex items-center gap-2 text-sm font-semibold text-slate-800" htmlFor="submit-tip-follow-up-attachments">
                    <Paperclip size={18} weight="bold" />
                    Add follow-up files
                  </label>
                  <input accept="image/*,.pdf,.doc,.docx,.txt,.csv" className="mt-3 block w-full text-sm text-slate-700" data-testid="submit-tip-follow-up-attachments-input" id="submit-tip-follow-up-attachments" multiple onChange={(event) => setFollowUpAttachments(Array.from(event.target.files || []))} type="file" />
                  {followUpAttachmentNames.length ? <p className="mt-3 text-xs text-slate-500" data-testid="submit-tip-follow-up-attachments-summary">{followUpAttachmentNames.join(", ")}</p> : null}
                </div>
                <Button className="rounded-none bg-[#D21404] px-5 py-6 text-xs font-bold uppercase tracking-[0.2em] text-white hover:bg-[#B31203]" data-testid="submit-tip-follow-up-submit-button" disabled={sendingFollowUp} type="submit">
                  {sendingFollowUp ? "Sending Update" : "Send Follow-Up"}
                </Button>
              </form>
            </section>
          ) : null}
        </aside>
      </section>
    </main>
  );
};